import React, { PropsWithChildren } from "react";
import { MAX_AMOUNT_OF_XP } from "shared/utils/constants";

import { toTenantIdHeader, useApiQuery } from "../../../api/use-api";
import { CHAT_COMPLETENESS_KEY } from "../../../types/query-keys";
import { useTenantId } from "../../../util/use-active-tenant-id";
import { cn } from "~/util/cn";

export const ChatCompletion: React.FC<
  PropsWithChildren<{
    conversationId?: string;
  }>
> = ({ children, conversationId }) => {
  const tenantId = useTenantId();

  const { data: percentage } = useApiQuery(
    "multiagent",
    (api) =>
      api.getMultiagentChatCompleteness({
        ...toTenantIdHeader(tenantId),
        conversationId: conversationId as string,
      }),
    CHAT_COMPLETENESS_KEY(tenantId.disease),
    undefined,
    { enabled: !!conversationId },
  );

  return (
    <div className="relative mx-auto w-full max-w-screen-lg">
      {percentage && percentage < 100 ? (
        <ChatCompletionLinearProgress
          percentage={percentage}
          className="absolute inset-x-2 -top-9 max-w-screen-sm sm:mx-auto"
        />
      ) : null}
      <div className="col-span-1 row-start-2 flex w-full flex-row justify-center gap-2">
        {children}
      </div>
    </div>
  );
};

const ChatCompletionLinearProgress: React.FC<{
  percentage?: number;
  className?: string;
}> = ({ percentage, className }) => {
  const value = percentage
    ? Math.floor(((percentage ?? 0) / 100) * MAX_AMOUNT_OF_XP)
    : 0;

  const progressPercentage = Math.floor(percentage ?? 0);
  const gridTemplateColumns = `${progressPercentage}% ${
    100 - progressPercentage
  }%`;

  return (
    <div
      className={cn(
        "flex items-center gap-4",
        "h-8 px-4",
        "border border-neutral-200 bg-base-100",
        "rounded-lg",
        className,
      )}
    >
      <div
        className="grid h-2 w-full"
        style={{
          gridTemplateColumns,
          transition: "grid-template-columns 1000ms ease-in-out",
        }}
      >
        <div className="h-full rounded-full rounded-r-none bg-primary" />
        <div className="h-full rounded-full rounded-l-none bg-neutral-100" />
      </div>
      <div className="h-4 text-xs font-medium text-neutral-500">
        <span>{value}</span>
        <span>{"XP"}</span>
      </div>
    </div>
  );
};
